import { useEffect, useState } from 'react';
import { formatCurrency} from 'utils/utils';
import { Button } from '../Button';
import Loader from 'components/Loader';
import RadioButton from '../RadioButton';
import TextBox from '../TextBox';
import ShoppingCartService from '../services/shopping-cart.service';
import { useErrorBoundary } from 'basket/ErrorBoundary';
import { SupplyOwnFabric } from '../SupplyOwnFabric';
import ProductQtyInput from 'components/ProductQtyInput';
import { OtherFabricSelector } from '../OtherFabricSelector';
import InformationOverlay from "../InformationOverlay";
import useInfoIcons from 'hooks/useInfoIcons';
import UnitConverter from 'components/UnitConverter';
import Checkbox from '../Checkbox';

export function BasketEditCushionsItem(props) {
    const { item, updateFabricItem, close } = props;
    const handleError = useErrorBoundary();
    const [price, setPrice] = useState(item.price);
    const [metres, setMetres] = useState(item.fabric.fabricMetres.toFixed(1) + 'm');
    const [styleChanged, setStyleChanged] = useState(false);
    const convertToBoolean = (value) => value == "1" || value == 1 ? true : (value == "0" || value == 0? false : value);
    const [fabricData, setFabricData] = useState({
        cushionNameID: item.fabric.cushionNameID,
        cushionID: item.fabric.cushionID,
        pipingYesNo: item.fabric.pipingYesNo,
        insertOptionId: item.fabric.insertOptionId,
        unitOfMeasurement: item.fabric.unitOfMeasurement ?? false,
        customisationRequests: item.fabric.customisationRequests,
        fabricId: item.fabricId,
        quantity: item.quantity,
        supplyOwnFabricNameOrDescription: item.supplyOwnFabricNameOrDescription,
        FabricRollWidth: item.supplyOwnFabricRollWidth,
        VerticalPatternRepeat: item.supplyOwnFabricPatternRepeat
    });
    const [loading, setLoading] = useState(false);

    const [itemTitle, setItemTitle] = useState(`${item.fabric.fabricName} in ${item.colour || ''} Fabric by ${item.fabric.supplier.name || ''}`);
    const title = () => item.supplyOwnFabric ? 'Edit Cushions in Supply Your Own Fabric' : `Edit Cushions - ${itemTitle}`;
    
    const [measurementsChecked, setMeasurementsChecked] = useState(false)
    const [showMeasurementsCheckError, setShowMeasurementsCheckError] = useState(false)

    const [cushionStyles, setCushionStyles] = useState([]);
    const [cushionSizes, setCushionSizes] = useState([]);

    const [errors, setErrors] = useState({});
    const [isSubmitDisabled, setSubmitDisabled] = useState(false);

    const [fabrics, setFabrics] = useState([])
    const [totalFabricsCount, setTotalFabricsCount] = useState(0)
    const [selectedFabric, setSelectedFabric] = useState(fabricData.fabricId)
                
    const infoIcons = useInfoIcons(item.productType);

    const fetchStyles = async () => {
        const responseStyles = await ShoppingCartService.create('options/5', {
            unitOfMeasurement: 3,
        }).catch(error => { handleError('Could not load item details. Please contact administrator'); });
        if (responseStyles) setCushionStyles(responseStyles);
    };

    const fetchSizes = async () => {
        const responseSizes = await ShoppingCartService.create('options/5', {
            id: fabricData.cushionNameID,
            unitOfMeasurement: fabricData.unitOfMeasurement,
        }).catch(_ => { handleError('Could not load item details. Please contact administrator'); });
        if (responseSizes){                
            setCushionSizes(responseSizes);
            if (styleChanged && responseSizes && responseSizes.length>0){
                setFabricData({ ...fabricData, cushionID: responseSizes[0].id });
            } 
        }
    };
    
    const handleStyleClick = (styleId) => {
        if (styleId != fabricData.cushionID){                
            setFabricData({ ...fabricData, cushionNameID: styleId });
            setStyleChanged(true);
        }
    };

    const handleSizeClick = (sizeId) => {
        setFabricData({ ...fabricData, cushionID: sizeId });
    };

    useEffect(() => {
        fetchSizes();
    }, [fabricData.cushionNameID]);

    const handleQuantityChange = (value) => handleInputChange({target: { name: 'quantity', value: value }})

    const handleInputChange = (e) => {        
        const { name, value } = e.target;
        const updatedData = { ...fabricData, [name]: value };
        setFabricData(updatedData);
    };

    const createItem = () => {
        return {
            ...item,
            fabricId: selectedFabric,
            quantity: fabricData.quantity,
            fabric: { 
                ...fabricData,
                unitOfMeasurement: convertToBoolean(fabricData.unitOfMeasurement),
                pipingYesNo: convertToBoolean(fabricData.pipingYesNo),
                insertOptionId: fabricData.insertOptionId,
            },
            supplyOwnFabricNameOrDescription: fabricData.supplyOwnFabricNameOrDescription,
            supplyOwnFabricPatternRepeat: fabricData.VerticalPatternRepeat,
            supplyOwnFabricRollWidth: fabricData.FabricRollWidth
        }
    }

    const getConvertedFabricData = (data) => {
        return {
            ...data,
            unitOfMeasurement: convertToBoolean(data.unitOfMeasurement),
            pipingYesNo: convertToBoolean(data.pipingYesNo),
            insertOptionId: data.insertOptionId,
        };
    };
    
    const handlerMeasurementCheck = () => {
        setMeasurementsChecked(!measurementsChecked)
        setShowMeasurementsCheckError(false)
    }

    const handleUpdate = () => {
        if (!measurementsChecked) {
            setShowMeasurementsCheckError(true)
            return
        }

        setLoading(true);
        updateFabricItem(createItem());
        setLoading(false);
    };

    const updatePrice = async () => {
        const item = createItem()

        setErrors([])
        
        if (item.supplyOwnFabric) {
            const isValidResponse = await fetch('/api/product/supply-own-fabric/validate', {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                body: JSON.stringify({
                    Description: item.supplyOwnFabricNameOrDescription,
                    VerticalPatternRepeat: Number(item.supplyOwnFabricPatternRepeat),
                    FabricRollWidth: Number(item.supplyOwnFabricRollWidth)
                })
            })
            
            if (isValidResponse.status !== 200) {
                const json = await isValidResponse.json();
                
                const lowercasedErrors = Object.entries(json).reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {});

                setErrors(prev => ({
                    ...prev,
                    ...lowercasedErrors
                }));
            }
        }

        const result = await ShoppingCartService.create('price', createItem())
            .catch(error => { handleError('Could not load item price. Please contact administrator'); });

        if (result) {
            setSubmitDisabled(false)
            setPrice(result.price);
            setMetres(result.fabricMetres.toFixed(1) + 'm');
        }
    }

    const loadFabrics = async () => {
        setLoading(true)
        const response = await ShoppingCartService.get('same-fabrics?fabricId=' + fabricData.fabricId)
        setFabrics(response.fabrics)
        setTotalFabricsCount(response.totalCount)
        setLoading(false)
    }

    const loadAllFabrics = async () => {
        setLoading(true)
        const response = await ShoppingCartService.get('same-fabrics?fabricId=' + fabricData.fabricId + '&showAll=true')
        setFabrics(response.fabrics)
        setTotalFabricsCount(response.totalCount)
        setLoading(false)
    }

    const validatePrice = async (item, callback) => {
        fetch('/api/product/cushions/validate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(item)
        })
        .then(response => {
            setErrors([]) // clear errors
            if (!response.ok) {
                return response.json().then(err => { throw err });
            }
            return response.json();
        })
        .then(() => {
            callback(item);
        })
        .catch(jsonResponse => {
            const lowercasedErrors = Object.entries(jsonResponse).reduce((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {});

            setErrors(prevErrors => ({
                ...prevErrors,
                ...lowercasedErrors
            }));
        });
    }

    useEffect(() => {
        validatePrice(getConvertedFabricData(fabricData), updatePrice)
    }, [fabricData.cushionNameID,
        fabricData.cushionID,
        fabricData.pipingYesNo,
        fabricData.insertOptionId,
        fabricData.fabricId,
        fabricData.quantity
    ]);

    useEffect(() => setSubmitDisabled(Object.keys(errors).length > 0), [errors])

    useEffect(() => {
        const foundFabric = fabrics.find(fabric => fabric.fabricId === selectedFabric);
        if (foundFabric){                    
            setItemTitle(foundFabric.fullName);
            validatePrice(fabricData, updatePrice)
        }
    }, [selectedFabric]);

    useEffect(() => {
        fetchStyles()
        fetchSizes()
        loadFabrics()
    }, []);

    return (
        <Loader isLoading={loading}>
            <form className="p-32 pb-54 bg3 measurements-change active-group sm-p-20">
                <div className="row">
                    <div className="col">
                        <div className="subtitle1 mb-16 sm-text-24 default-text">{title() + ' '}                             
                            {infoIcons && infoIcons.length > 0  && (
                                <span className="paragraph-info-icon-wrapper">                                        
                                    <InformationOverlay item={infoIcons.find(o => o.name === "Description")} />
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-auto ps-24">
                        <span
                            className="curtains-icon curtains-icon-close size-24 show-btn hide-btn"
                            onClick={() => close()}
                        ></span>
                    </div>
                </div>
                
                { item.supplyOwnFabric && <SupplyOwnFabric fabricData={fabricData} errors={errors} handleInputChange={handleInputChange} onBlur={() => validatePrice(fabricData, updatePrice)} /> }
                { !item.supplyOwnFabric &&  <OtherFabricSelector fabrics={fabrics} totalFabricsCount={totalFabricsCount} loadAllFabrics={loadAllFabrics} selectedFabric={selectedFabric} setSelectedFabric={setSelectedFabric} />}

                {/* Cushion Styles */}
                <div className="mb-16">
                    <div className="body-text3 gray mb-8 align-center gap_3">Cushion Style:
                        {infoIcons && infoIcons.length > 0  && (
                            <InformationOverlay item={infoIcons.find(o => o.name === "Style")} />
                        )}
                    </div>
                    <div className="thumbnails choice-thumbnails gap-24 fit-content sm-gap-12">
                        {cushionStyles && cushionStyles.map((style) => (
                            <div
                                key={style.id}
                                className={`choice-heading choice-cushion-style max-width-96 ${
                                    style.id === fabricData.cushionNameID ? 'active' : ''
                                }`}
                                onClick={() => handleStyleClick(style.id)}
                            >
                                <div className="thumbnail pointer mb_5 rel">
                                    <img src={style.imageUrl} className="max-height-97" alt={style.name} />
                                    <span className="curtains-icon curtains-icon-Check"></span>
                                </div>
                                <div className="body-text6 ps_5">{style.name}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <hr className="secondary-line mb-16" />

                {/* Cushion Sizes and Measurements */}
                <div>
                    <div className='row'>
                        <div className="col">
                            <div className="body-text3 gray mb-8">Measurements:</div>
                        </div>
                        <div className='col-auto'>
                            {infoIcons && infoIcons.length > 0  && (
                                <InformationOverlay item={infoIcons.find(o => o.name === "HowToMeasure")} clickTitle="How to Measure?" />
                            )}
                        </div>                            
                    </div>

                    {/* Cushion Sizes */}
                    <div id="cushionSizes" className='four-columns gap-8 row-gap-16 sm-gap-8 mb-16'>
                        {cushionSizes && cushionSizes.map((size) => (
                            <div
                                key={size.id}
                                className={`light-accent-box pointer make-active ${size.id === fabricData.cushionID ? 'active' : ''}`}
                                onClick={() => handleSizeClick(size.id)}
                            >
                                <span className="curtains-icon curtains-icon-Check"></span> {size.name}
                            </div>
                        ))}
                    </div>

                    <UnitConverter />
                </div>
                <hr className="secondary-line mb-16"></hr>

                {/* Piping */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Piping:
                        {infoIcons && infoIcons.length > 0  && (
                            <InformationOverlay item={infoIcons.find(o => o.name === "Piping")} />
                        )}
                    </div>
                    <div className="d-flex gap-32 mb-16">
                        <RadioButton
                            name="pipingYesNo"
                            label={'Yes'}
                            value={1}
                            checked={fabricData.pipingYesNo == 1}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            name="pipingYesNo"
                            label={'No'}
                            value={0}
                            checked={fabricData.pipingYesNo == 0}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <div className="dashed-line mb-16"></div>

                {/* Insert Filling */}
                <div>
                    <div className="body-text3 gray mb-8 align-center gap_3">Insert Filling:
                        {infoIcons && infoIcons.length > 0  && (
                            <InformationOverlay item={infoIcons.find(o => o.name === "Filling")} />
                        )}
                    </div>
                    <div className="d-flex gap-32 mb-16">
                        <RadioButton
                            name="insertOptionId"
                            label={'Feather'}
                            value={1}
                            checked={fabricData.insertOptionId == 1}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            name="insertOptionId"
                            label={'Hollowfibre'}
                            value={0}
                            checked={fabricData.insertOptionId == 0}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            name="insertOptionId"
                            label={'No insert'}
                            value={2}
                            checked={fabricData.insertOptionId == 2}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <hr className="secondary-line"/>

                {/* Customization */}
                <div style={{marginBottom: '16px'}}>
                    <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Cushion Customisation:</div>
                    <div className="max-width-410 mb-16 sm-text-14">If you have special requests, write below or <a href="/contact-us" className="underline">contact us</a> <a href="tel:0345 8620743" className="underline">0345 8620743</a>.</div>
                    <TextBox
                        name="customisationRequests"
                        value={fabricData.customisationRequests}
                        onChange={handleInputChange}
                        placeholder='e.g. Trim or borders'
                        onBlur={() => validatePrice(fabricData, updatePrice)}
                    />
                    {errors.Customization && (
                        <span className="field-validation-error">{errors.Customization}</span>
                    )}
                </div>

                <Checkbox 
                    checked={measurementsChecked} 
                    label={'I have checked all measurements'}
                    value={!measurementsChecked}
                    onChange={handlerMeasurementCheck}
                    requiredMessage={''}
                    name={'measurementsChecked'} />
                { showMeasurementsCheckError && <div className="text-danger m-8">Please confirm you have checked everything.</div> }

                {
                    item.supplyOwnFabric && 
                        <div>
                            <div className="body-text3 gray mt-32 mb-8 align-center gap_3">Quantity of fabric per item to be supplied by you:</div>
                            <TextBox name='sof-fabric-length' value={metres} className='max-width-150 mb-16' />
                        </div>
                }

                <div className="dashed-line mt-16"></div>

                {/* TOTAL */}
                <div>
                    <div className="subtitle2 mb-8 mt-16">TOTAL: <span className="body-text1">{formatCurrency(price, 2)}</span></div>
                    <div className="body-text6">Delivery for made-to-measure cushions: {item.deliveryContent} weeks.</div>
                </div>

                <div className="mb-16 mt-32" id="updateProductQuantity">
                    <div className="dashed-line mb-24"></div>
                    <div className="body-text3 gray mb-16">Quantity:</div>
                    
                    <ProductQtyInput
                        digits={0}
                        initValue={fabricData.quantity} 
                        onlyInt={true}
                        onChange={handleQuantityChange} 
                        onBlur={() => validatePrice(fabricData, updatePrice)}
                    />
                    {errors.Quantity && (
                        <span className="field-validation-error">{errors.Quantity}</span>
                    )}
                </div>

                <div className="d-flex gap-16 mb-16 sm-column md-column sm-gap-8 sm-mb-0">
                    <div className="w-100 show-btn hide-btn">
                        <Button name="UPDATE" onClick={handleUpdate} disabled={isSubmitDisabled || Object.keys(errors).length > 0} />
                    </div>
                </div>
            </form>
        </Loader>
    );
}